import { useContext, useState } from 'react'
import { useFirestoreGeneralOrderBy, useFirestoreUsers } from '../../../firebase/useFirestore'
import { Auth } from '../../../StateManagment/Auth'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import Table from '../../common/table/Table.jsx'
import TableCell from '../../common/table/TableCell.jsx'
import ProjectNoteModal from './ProjectNoteModal'
import DeleteModal from '../../common/DeleteModal'
import Tooltip from '../../common/Tooltip'
import ReadOnlyGuard from '../../common/ReadOnlyGuard'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import softDeleteDoc from '../../../helpers/softDeleteDoc'
import NoteBodyCell from '../../notes/NoteBodyCell'

const DATE_OPTIONS = { year: 'numeric', month: 'numeric', day: 'numeric' }

/**
 * Notities die de beheertenant vastlegt bij een klantproject — afspraken uit
 * overleggen. De documenten leven in de EIGEN omgeving (`CompagnyID: client`)
 * en wijzen met `ProjectID` naar het project; de klant ziet ze niet.
 *
 * AI-CONTEXT: See docs/features/deccos-projectafspraken.md
 *
 * @param {object} props
 * @param {string} [props.projectId] - CompagnyID van het project waar de notities bij horen
 */
const ProjectNotes = ({ projectId }) => {
    // Context
    const [auth] = useContext(Auth)
    const [, setSaved] = useContext(SavedIcon)

    // State
    const [modalOpen, setModalOpen] = useState(false)
    const [editNote, setEditNote] = useState(/** @type {{docid: string, Title?: string, Body?: string} | null} */ (null))
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('')
    const [deleteName, setDeleteName] = useState('deze notitie')

    // Firestore — '' is het signaal voor "nog geen project", dan draait er geen query.
    // Sorteren op datum, nieuwste boven: deze lijst kent geen sleepvolgorde, dus
    // het volgnummer zegt niets over de gewenste volgorde.
    const notes = useFirestoreGeneralOrderBy('ProjectNotes', 'ProjectID', projectId || '', 'Timestamp', 'desc')
    // Eén lijst teamleden voor de hele tabel. Een naam-component per rij zou
    // twee luisteraars per notitie openen — en die lezen bovendien de hele
    // Users-collectie, buiten de tenant om.
    const users = useFirestoreUsers()

    const rows = Array.isArray(notes) ? notes : []
    const teamUsers = Array.isArray(users) ? users : []

    const nameFor = (userId) => {
        const user = teamUsers.find((candidate) => candidate.ID === userId)
        return user?.UserName || user?.Email || 'Onbekend'
    }

    const openCreateModal = () => {
        setEditNote(null)
        setModalOpen(true)
    }

    const openEditModal = (note) => {
        setEditNote(note)
        setModalOpen(true)
    }

    const deleteModalHandler = (e) => {
        setDeleteDocid(e.currentTarget.dataset.docid)
        setDeleteName(e.currentTarget.dataset.deletename || 'deze notitie')
        setDeleteModal(true)
    }

    const deleteNote = async () => {
        await softDeleteDoc({ collection: 'ProjectNotes', docid: deleteDocid, authId: auth?.ID })
        setSaved('flex')
    }

    return (
        <div className="project-afspraken-block">
            <ReadOnlyGuard edit="tasks.edit">
                {(canEdit) => (
                    <Table
                        columns={[
                            { key: 'title', label: 'TITEL' },
                            { key: 'date', label: 'DATUM', searchable: false },
                            { key: 'author', label: 'AUTEUR', searchable: false },
                            { key: 'body', label: 'NOTITIE', searchable: false },
                            { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                        ]}
                        items={rows}
                        getItemKey={(note) => note.ID}
                        renderRow={(note) => (
                            <>
                                <TableCell>
                                    <button
                                        type="button"
                                        className="project-afspraken-link"
                                        onClick={() => openEditModal(note)}
                                    >
                                        {note.Title || 'Naamloze notitie'}
                                    </button>
                                </TableCell>
                                <TableCell>
                                    {note.Timestamp?.toDate
                                        ? note.Timestamp.toDate().toLocaleDateString('nl-NL', DATE_OPTIONS)
                                        : '—'}
                                </TableCell>
                                <TableCell>{nameFor(note.User)}</TableCell>
                                <TableCell>
                                    <NoteBodyCell body={note.Body} />
                                </TableCell>
                                <TableCell>
                                    {canEdit && (
                                        <Tooltip content={'Notitie verwijderen'} top="-60px" width={'30px'}>
                                            <DeleteOutlinedIcon
                                                className="table-delete-icon"
                                                data-docid={note.docid}
                                                data-deletename={note.Title}
                                                onClick={deleteModalHandler}
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </>
                        )}
                        searchAccessor={(note) => note?.Title || ''}
                        searchPlaceholder="Zoeken op titel"
                        emptyMessage="Nog geen notities vastgelegd."
                        addRow={canEdit ? {
                            toolTipContent: 'Notitie toevoegen',
                            onClick: openCreateModal,
                        } : undefined}
                    />
                )}
            </ReadOnlyGuard>

            {modalOpen && (
            <ProjectNoteModal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                projectId={projectId}
                note={editNote || undefined}
                position={rows.length + 1}
            />
            )}

            <DeleteModal
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                deleteItem={deleteNote}
                deleteName={deleteName}
                softDelete
            />
        </div>
    )
}

export default ProjectNotes
